"use client"

import React, { useEffect, useMemo, useState } from "react"
import { FiPlus, FiEdit, FiTrash2, FiEye, FiUploadCloud } from "react-icons/fi"
import toast from "react-hot-toast"
import api from "../../libs/axios"
import Breadcrumb from "../../utils/breadcrumbs"
import { ListLoader } from "../../utils/loader"
import ConfirmationModal from "../../ui/confirmation-modal"

interface TickerUi {
    id: number
    title: string
    description?: string | null
    features?: string[]
    html_content?: string | null
    ui_images?: string[]
    status: "active" | "inactive"
    /** Lower shows first, in this list and in the app's ticker picker. */
    display_order?: number
    created_at?: string
    updated_at?: string
}

type FormMode = "add" | "edit"

const statusStyle = (s: string) =>
    s === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"

const Tickers: React.FC = () => {
    const [tickers, setTickers] = useState<TickerUi[]>([])
    const [loading, setLoading] = useState(false)
    const [statusFilter, setStatusFilter] = useState("")

    // Add / Edit form
    const [formOpen, setFormOpen] = useState(false)
    const [formMode, setFormMode] = useState<FormMode>("add")
    const [editId, setEditId] = useState<number | null>(null)
    const [fTitle, setFTitle] = useState("")
    const [fDescription, setFDescription] = useState("")
    const [fStatus, setFStatus] = useState<"active" | "inactive">("active")
    const [fOrder, setFOrder] = useState<string>("100")
    const [fHtml, setFHtml] = useState<string>("")
    const [fFileName, setFFileName] = useState<string>("")
    const [saving, setSaving] = useState(false)

    // View / preview
    const [viewTicker, setViewTicker] = useState<TickerUi | null>(null)

    // Delete
    const [deleteTarget, setDeleteTarget] = useState<TickerUi | null>(null)
    const [deleting, setDeleting] = useState(false)

    const fetchTickers = async () => {
        setLoading(true)
        try {
            const res: any = await api.get(`/ticker-uis?status=all`)
            setTickers(res?.data?.ticker_uis || [])
        } catch (e) {
            toast.error("Failed to load tickers")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchTickers() }, [])

    const visible = useMemo(
        () => tickers.filter((t) => !statusFilter || t.status === statusFilter),
        [tickers, statusFilter]
    )

    const openAdd = () => {
        setFormMode("add"); setEditId(null)
        setFTitle(""); setFDescription(""); setFStatus("active"); setFHtml(""); setFFileName(""); setFOrder("100")
        setFormOpen(true)
    }

    const openEdit = (t: TickerUi) => {
        setFormMode("edit"); setEditId(t.id)
        setFTitle(t.title); setFDescription(t.description || ""); setFStatus(t.status); setFOrder(String(t.display_order ?? 100))
        setFHtml(t.html_content || ""); setFFileName("")
        setFormOpen(true)
    }

    const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const name = file.name.toLowerCase()
        if (!name.endsWith(".html") && !name.endsWith(".htm")) {
            toast.error("Please upload a .html file")
            return
        }
        try {
            const text = await file.text()
            setFHtml(text)
            setFFileName(file.name)
            if (!fTitle.trim()) setFTitle(file.name.replace(/\.(html?|HTML?)$/i, ""))
            toast.success("HTML loaded from file")
        } catch {
            toast.error("Could not read the file")
        }
    }

    const submitForm = async () => {
        if (!fTitle.trim()) { toast.error("Title is required"); return }
        const order = Number(fOrder)
        if (fOrder.trim() === "" || !Number.isInteger(order) || order < 0) { toast.error("Order must be a whole number, 0 or more"); return }
        if (formMode === "add" && !fHtml.trim()) { toast.error("Upload the ticker .html file"); return }
        setSaving(true)
        try {
            const payload: any = {
                title: fTitle.trim(),
                description: fDescription.trim() || null,
                status: fStatus,
                display_order: order,
            }
            // Only send html_content when we have new content (edit keeps existing if untouched).
            if (fHtml) payload.html_content = fHtml
            if (formMode === "add") {
                await api.post(`/ticker-uis`, payload)
                toast.success("Ticker created")
            } else if (editId != null) {
                await api.patch(`/ticker-uis/${editId}`, payload)
                toast.success("Ticker updated")
            }
            setFormOpen(false)
            fetchTickers()
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Failed to save ticker")
        } finally {
            setSaving(false)
        }
    }

    const toggleStatus = async (t: TickerUi) => {
        const next = t.status === "active" ? "inactive" : "active"
        try {
            await api.patch(`/ticker-uis/${t.id}/status`, { status: next })
            setTickers((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: next } : x)))
            toast.success(`Marked ${next}`)
        } catch {
            toast.error("Failed to update status")
        }
    }

    const confirmDelete = async () => {
        if (!deleteTarget) return
        setDeleting(true)
        try {
            await api.delete(`/ticker-uis/${deleteTarget.id}`)
            toast.success(`${deleteTarget.title} deleted`)
            setDeleteTarget(null)
            fetchTickers()
        } catch (e: any) {
            // 409 = ticker bound to a stream; BE tells us to set inactive instead.
            toast.error(e?.response?.data?.message || "Failed to delete ticker")
            setDeleteTarget(null)
        } finally {
            setDeleting(false)
        }
    }

    return (
        <div className="w-full min-h-screen bg-white p-4 md:p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Live Stream Tickers
                </h2>
                <Breadcrumb />
            </div>

            <p className="text-sm text-gray-500 mb-4">
                Ticker overlays shown on the OBS live-stream scoreboard. Upload a <code>.html</code> overlay to add one.
                Only <span className="font-medium">active</span> tickers are selectable by streamers.
            </p>

            <div className="w-full bg-white rounded-lg shadow border">
                <div className="flex justify-between items-center gap-2 p-4 w-full sticky top-0 bg-white">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    >
                        <option value="">All statuses</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                    <button
                        onClick={openAdd}
                        className="flex gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg items-center"
                    >
                        <FiPlus className="w-5 h-5" />
                        <span className="hidden sm:block">Add Ticker</span>
                    </button>
                </div>

                <div className="overflow-x-auto min-h-[60vh]">
                    <table className="w-full min-w-full divide-y divide-gray-200 border border-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase border-r">Order</th>
                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase border-r">Title</th>
                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase border-r">Description</th>
                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase border-r">Status</th>
                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase border-r">Updated</th>
                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase border-r">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <ListLoader />
                            ) : visible.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-10 text-gray-400">No tickers</td></tr>
                            ) : (
                                visible.map((t) => (
                                    <tr key={t.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 border-r text-sm text-gray-700">{t.display_order ?? 100}</td>
                                        <td className="px-4 py-4 border-r">
                                            <div className="font-semibold text-gray-900">{t.title}</div>
                                            {!t.html_content && <div className="text-xs text-amber-600">no html</div>}
                                        </td>
                                        <td className="px-4 py-4 border-r">
                                            <div className="text-sm text-gray-600 max-w-md truncate">{t.description || "—"}</div>
                                        </td>
                                        <td className="px-4 py-4 border-r">
                                            <button
                                                onClick={() => toggleStatus(t)}
                                                className={`px-2 py-1 capitalize text-xs rounded-full ${statusStyle(t.status)}`}
                                                title="Click to toggle"
                                            >
                                                {t.status}
                                            </button>
                                        </td>
                                        <td className="px-4 py-4 border-r text-sm text-gray-500">
                                            {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="px-4 py-4 border-r">
                                            <div className="flex space-x-3">
                                                <button onClick={() => setViewTicker(t)} className="text-gray-600 hover:text-gray-900" title="Preview">
                                                    <FiEye className="w-5 h-5" />
                                                </button>
                                                <button onClick={() => openEdit(t)} className="text-blue-600 hover:text-blue-800" title="Edit">
                                                    <FiEdit className="w-5 h-5" />
                                                </button>
                                                <button onClick={() => setDeleteTarget(t)} className="text-red-600 hover:text-red-800" title="Delete">
                                                    <FiTrash2 className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add / Edit modal */}
            {formOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !saving && setFormOpen(false)}>
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="px-5 py-4 border-b flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900">{formMode === "add" ? "Add Ticker" : "Edit Ticker"}</h3>
                            <button className="text-gray-400 hover:text-gray-700" onClick={() => !saving && setFormOpen(false)}>✕</button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                                <input value={fTitle} onChange={(e) => setFTitle(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. Classic" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea value={fDescription} onChange={(e) => setFDescription(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} placeholder="Optional" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Ticker HTML {formMode === "add" ? "*" : "(upload to replace)"}
                                </label>
                                <label className="flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg text-sm text-gray-600 cursor-pointer hover:bg-gray-50 w-fit">
                                    <FiUploadCloud className="w-4 h-4" />
                                    <span>Choose .html file</span>
                                    <input type="file" accept=".html,.htm,text/html" className="hidden" onChange={onPickFile} />
                                </label>
                                {(fFileName || fHtml) && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        {fFileName ? `Loaded: ${fFileName} ` : "Existing HTML kept "}
                                        ({fHtml.length} chars)
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select value={fStatus} onChange={(e) => setFStatus(e.target.value as any)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm">
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                                <input type="number" min={0} step={1} value={fOrder} onChange={(e) => setFOrder(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                                <p className="text-xs text-gray-500 mt-1">Lower shows first in the app. Tickers left at 100 come after, oldest first.</p>
                            </div>
                        </div>
                        <div className="px-5 py-4 border-t flex justify-end gap-2">
                            <button onClick={() => setFormOpen(false)} disabled={saving}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-50">Cancel</button>
                            <button onClick={submitForm} disabled={saving}
                                className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm disabled:opacity-50">
                                {saving ? "Saving…" : formMode === "add" ? "Create" : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview modal */}
            {viewTicker && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setViewTicker(null)}>
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
                        <div className="px-5 py-4 border-b flex items-center justify-between">
                            <h3 className="font-semibold text-gray-900">{viewTicker.title} — preview</h3>
                            <button className="text-gray-400 hover:text-gray-700" onClick={() => setViewTicker(null)}>✕</button>
                        </div>
                        <div className="p-4">
                            {viewTicker.html_content ? (
                                <div className="w-full rounded-lg overflow-hidden border bg-[#111]" style={{ aspectRatio: "16 / 9" }}>
                                    <iframe
                                        title="ticker-preview"
                                        sandbox="allow-scripts"
                                        srcDoc={viewTicker.html_content}
                                        className="origin-top-left border-0"
                                        style={{ width: "1920px", height: "1080px", transform: "scale(0.5)" }}
                                    />
                                </div>
                            ) : (
                                <div className="text-center text-gray-400 py-10">This ticker has no HTML.</div>
                            )}
                            <p className="text-xs text-gray-500 mt-2">
                                Layout preview only — live score data appears when the ticker is bound to a running match stream.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {deleteTarget && (
                <ConfirmationModal
                    isOpen={!!deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={confirmDelete}
                    title="Delete Ticker"
                    description={`Permanently delete "${deleteTarget.title}"? If it is bound to any stream this will be blocked — set it inactive instead.`}
                    loading={deleting}
                />
            )}
        </div>
    )
}

export default Tickers
